import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  User1Line,
  Settings3Line,
  ExitDoorLine,
  MonitorLine,
  CheckLine,
} from '@mingcute/react';

import { cn } from '../lib/cn';

/**
 * Klaret UserMenu (handoff §10.8 Menu).
 *
 * Avatar trigger → a panel anchored bottom-end to the avatar. Built with
 * useState + createPortal (NOT React Aria Components): the topbar <header> uses
 * backdrop-filter, which creates a stacking context that traps an in-flow panel
 * behind the page, and RAC's collection <Menu> silently drops everything when a
 * non-collection node (the identity header) sits among the items. The portal +
 * fixed-position pattern mirrors NotificationBell — proven, deterministic, and
 * free of the duplicate-React/submenu footguns RAC overlays carry here.
 *
 * Theme + language are rendered inline (no hover submenus) so toggling them
 * keeps the panel open; profile/settings/sign-out close it after acting.
 */

export type ThemeValue = 'white' | 'black' | 'system';

export interface UserMenuLanguage {
  code: string;
  label: string;
}

export interface UserMenuProps {
  name: string;
  email?: string;
  avatarUrl?: string;
  initials?: string;

  theme?: ThemeValue;
  onThemeChange?: (theme: ThemeValue) => void;

  language?: string;
  languages?: UserMenuLanguage[];
  onLanguageChange?: (code: string) => void;

  onProfile?: () => void;
  onSettings?: () => void;
  onSignOut?: () => void;

  /** Optional label overrides (defaults are English). */
  labels?: Partial<{
    profile: string;
    settings: string;
    theme: string;
    white: string;
    black: string;
    system: string;
    language: string;
    signOut: string;
  }>;

  className?: string;
}

function deriveInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
}

const DEFAULT_LABELS = {
  profile: 'Profile',
  settings: 'Settings',
  theme: 'Theme',
  white: 'White',
  black: 'Black',
  system: 'System',
  language: 'Language',
  signOut: 'Sign out',
};

const rowClass = cn(
  'flex w-full cursor-pointer items-center gap-2 rounded-[var(--tvl-r2)] px-2 py-1.5',
  'text-left text-[14px] leading-[20px] text-[var(--tvl-ink)] outline-none transition-colors',
  'hover:bg-[var(--tvl-wash-hover)] focus-visible:bg-[var(--tvl-wash-hover)]',
  'focus-visible:outline-2 focus-visible:outline-[color:var(--tvl-focus)] focus-visible:-outline-offset-2',
);

const THEME_OPTIONS: {
  value: ThemeValue;
  labelKey: 'white' | 'black' | 'system';
  /** Whether the chip spans both grid columns (System sits on its own row). */
  span2?: boolean;
  /** Static preview swatch — always shows ITS palette, regardless of active theme. */
  swatchBg: string;
  swatchDot: string;
}[] = [
  // The Klaret light/dark pair was retired — the app is monochrome. `system` stays because
  // following the OS is a native behaviour worth keeping; it now resolves to white/black.
  { value: 'system', labelKey: 'system', span2: true, swatchBg: 'var(--tvl-canvas)', swatchDot: 'var(--tvl-accent)' },
  { value: 'white', labelKey: 'white', swatchBg: '#faf9f5', swatchDot: '#141413' },
  { value: 'black', labelKey: 'black', swatchBg: '#1f1e1d', swatchDot: '#faf9f5' },
];

export const UserMenu: React.FC<UserMenuProps> = ({
  name,
  email,
  avatarUrl,
  initials,
  theme = 'system',
  onThemeChange,
  language,
  languages,
  onLanguageChange,
  onProfile,
  onSettings,
  onSignOut,
  labels,
  className,
}) => {
  const L = { ...DEFAULT_LABELS, ...labels };
  const inits = initials ?? deriveInitials(name);

  const [open, setOpen] = React.useState(false);
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const toggle = React.useCallback(() => {
    if (buttonRef.current) setRect(buttonRef.current.getBoundingClientRect());
    setOpen((o) => !o);
  }, []);

  const close = React.useCallback(() => setOpen(false), []);

  // Run a navigation action, then close the panel.
  const act = React.useCallback(
    (fn?: () => void) => {
      fn?.();
      setOpen(false);
    },
    [],
  );

  // Close on outside-click + Escape; reposition while open.
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const reposition = () => {
      if (buttonRef.current) setRect(buttonRef.current.getBoundingClientRect());
    };
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label="User menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        className={cn(
          'inline-flex items-center justify-center rounded-full outline-none',
          'focus-visible:outline-2 focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-2',
          className,
        )}
      >
        <span
          className={cn(
            'inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full',
            'bg-[var(--tvl-sunken)] text-[var(--tvl-ink-2)]',
            'text-[11px] font-[600] leading-none',
          )}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt={name} className="h-full w-full object-cover" />
          ) : (
            inits || '?'
          )}
        </span>
      </button>

      {open && rect
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              aria-label="User menu"
              style={{
                position: 'fixed',
                top: rect.bottom + 6,
                right: Math.max(12, window.innerWidth - rect.right),
              }}
              className={cn(
                'z-[var(--tvl-z-popover)] min-w-[224px] max-w-[calc(100vw-1.5rem)]',
                'rounded-[var(--tvl-r3)] border border-[var(--tvl-hairline)]',
                'bg-[var(--tvl-raised)] p-1 shadow-[var(--tvl-e2)]',
              )}
            >
              {/* Identity header — non-interactive */}
              <div className="flex flex-col gap-0.5 px-2 py-1.5">
                <span className="truncate text-[14px] font-[500] leading-[20px] text-[var(--tvl-ink)]">
                  {name}
                </span>
                {email ? (
                  <span className="truncate text-[12px] leading-[16px] text-[var(--tvl-ink-3)]">
                    {email}
                  </span>
                ) : null}
              </div>

              <div className="my-1 h-px bg-[var(--tvl-hairline)]" />

              {onProfile ? (
                <button type="button" role="menuitem" className={rowClass} onClick={() => act(onProfile)}>
                  <User1Line size={16} color="var(--tvl-ink-2)" aria-hidden />
                  <span>{L.profile}</span>
                </button>
              ) : null}

              {onSettings ? (
                <button type="button" role="menuitem" className={rowClass} onClick={() => act(onSettings)}>
                  <Settings3Line size={16} color="var(--tvl-ink-2)" aria-hidden />
                  <span>{L.settings}</span>
                </button>
              ) : null}

              {/* Theme — inline swatch grid (keeps panel open). System spans the
                  row; the Klaret light/dark pair and the monochrome white/black
                  pair sit in two columns. Each swatch always previews its OWN
                  palette so the choice reads at a glance. */}
              {onThemeChange ? (
                <div className="px-2 pb-1 pt-2">
                  <div className="mb-1.5 text-[11px] font-[600] uppercase tracking-[0.04em] text-[var(--tvl-ink-3)]">
                    {L.theme}
                  </div>
                  <div className="grid grid-cols-2 gap-1" role="group" aria-label={L.theme}>
                    {THEME_OPTIONS.map(({ value, labelKey, span2, swatchBg, swatchDot }) => {
                      const active = theme === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          aria-pressed={active}
                          onClick={() => onThemeChange(value)}
                          className={cn(
                            'flex items-center gap-2 rounded-[var(--tvl-r2)] px-2 py-1.5',
                            'text-[12px] leading-none outline-none transition-colors',
                            'focus-visible:outline-2 focus-visible:outline-[color:var(--tvl-focus)] focus-visible:-outline-offset-2',
                            span2 ? 'col-span-2' : '',
                            active
                              ? 'bg-[var(--tvl-selected)] text-[var(--tvl-ink)]'
                              : 'text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)]',
                          )}
                        >
                          <span
                            aria-hidden
                            className="inline-flex h-4 w-4 shrink-0 items-center justify-center overflow-hidden rounded-[var(--tvl-r1)] border border-[var(--tvl-hairline)]"
                            style={{ background: swatchBg }}
                          >
                            {value === 'system' ? (
                              <MonitorLine size={11} color="var(--tvl-ink-2)" />
                            ) : (
                              <span
                                className="block h-1.5 w-1.5 rounded-full"
                                style={{ background: swatchDot }}
                              />
                            )}
                          </span>
                          <span className="truncate">{L[labelKey]}</span>
                          {active ? (
                            <span className="ml-auto inline-flex shrink-0">
                              <CheckLine size={13} color="var(--tvl-accent)" aria-hidden />
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {/* Language — inline rows (keeps panel open) */}
              {onLanguageChange && languages && languages.length > 0 ? (
                <div className="px-2 pb-1 pt-2">
                  <div className="mb-1 text-[11px] font-[600] uppercase tracking-[0.04em] text-[var(--tvl-ink-3)]">
                    {L.language}
                  </div>
                  <div className="flex flex-col">
                    {languages.map((lang) => {
                      const active = !!language && language.startsWith(lang.code);
                      return (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => onLanguageChange(lang.code)}
                          className={cn(rowClass, 'justify-between')}
                        >
                          <span className={active ? 'font-[500] text-[var(--tvl-accent)]' : undefined}>
                            {lang.label}
                          </span>
                          {active ? (
                            <CheckLine size={16} color="var(--tvl-accent)" aria-hidden />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {onSignOut ? (
                <>
                  <div className="my-1 h-px bg-[var(--tvl-hairline)]" />
                  <button
                    type="button"
                    role="menuitem"
                    className={cn(rowClass, 'text-[var(--tvl-danger)] hover:bg-[var(--tvl-danger-tint)]')}
                    onClick={() => act(onSignOut)}
                  >
                    <ExitDoorLine size={16} color="var(--tvl-danger)" aria-hidden />
                    <span>{L.signOut}</span>
                  </button>
                </>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};
UserMenu.displayName = 'UserMenu';
